/**
 * DashboardOverview — F-pattern layout
 *   Row 1  Greeting + KPI strip
 *   Row 2  Urgent action banner
 *   Row 3  Left: recommended actions + unified job matches | Right: ATS + gamification
 *   Row 4  Analytics: compliance trend + job search activity
 */

import { Capacitor } from '@capacitor/core';
import { motion } from 'framer-motion';
import {
  Briefcase,
  Calendar,
  Check,
  CheckCircle2,
  FileText,
  MapPin,
  MessageCircle,
  Mic,
  Search,
  Star,
  Tag,
  X,
} from 'lucide-react';
import type { TFunction } from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CompanyLogoShared from '../../../components/CompanyLogo';
import LoadingSpinner from '../../../components/LoadingSpinner';
import {
  DashboardGreetingHeader,
  DashboardKpiStrip,
  DashboardRecommendedActions,
} from './DashboardGreeting';
import type { DailySnapshot, RecommendedAction } from './DashboardGreeting';
import DailyBriefCard from './DailyBriefCard';
import DashboardGamification from './DashboardGamification';
import HiddenOpportunities from './HiddenOpportunities';
import JobMatchExplainerModal from './JobMatchExplainerModal';
import MatchScoreButton from './MatchScoreButton';
import { MobileNativeHeader } from '../../../components/mobile';
import { supabase } from '../../../lib/supabase';
import {
  getApplicationStatistics,
  getJobApplications,
} from '../../../services/applicationTrackingService';
import { getCalendarReminders } from '../../../services/calendarRemindersService';
import { buildDailyBriefViewModel } from '../../../services/dailyBriefService';
import { getJobReadiness } from '../../../services/dashboardInsightsService';
import {
  getOptimizationHistory,
  getOptimizationStats,
} from '../../../services/optimizationService';
import { getCurrentUserId } from '../../../utils/authUtils';
import { getTopJobsForUser } from '../../recommendations/engine/recommendationEngine';
import type { ScoreBreakdown } from '../../recommendations/types';
import { useUserProfile } from '../hooks/useUserProfile';
import { computeProfileCompletenessPct } from './ProfileCompletenessMeter';
import { buildHiddenOpportunities, jobListToHiddenSources } from '../utils/buildHiddenOpportunities';
import type { JobApplication } from '../../../services/applicationTrackingService';
import {
  countFollowUpsDue,
  countInterviews,
  countNewMatchesToday,
  daysSinceDate,
  pickFollowUpCandidate,
} from '../utils/jobHuntMetrics';
import { mapScoreToBreakdown } from '../utils/mapScoreToBreakdown';
import { writeAgentOptimizeHandoff } from './Jobs/agentOptimizeHandoff';
import type { Job } from './JobCard';

import { cleanSkills } from './Jobs/helpers';
import type { DashboardPage } from './Layout';
import {
  DashboardAreaTrendChart,
  DashboardStackedBarChart,
  scoreColor,
} from './charts';

/** Subset of Jobs tab select â€” only fields used by `JobMatchCard` */
const PEERLIST_JOB_SELECT =
  'id, title, company, location, job_type, salary_min, salary_max, source_channel, skills, must_have_skills, last_seen_at';

// â”€â”€â”€ Types â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface DashboardOverviewProps {
  onNavigate: (page: DashboardPage) => void;
}

type JobMatch = {
  id: string;
  title: string;
  company: string;
  location: string;
  score: number;
  missingKeywords: string[];
  matchedSkills: string[];
  employmentType?: string;
  salaryMin?: number;
  salaryMax?: number;
  sourceChannel?: string;
  generatedAt?: string;
  lastSeenAt?: string;
  breakdown?: ScoreBreakdown;
  whyMatched?: string;
};

type ActivityItem = {
  id: string;
  kind: 'applied' | 'optimized' | 'interview';
  title: string;
  company: string;
  date: string;
};

type BarPoint = { opt: number; app: number; off: number };

// â”€â”€â”€ Constants â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function buildRollingMonthLabels(locale: string): string[] {
  const out: string[] = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - i);
    out.push(d.toLocaleString(locale, { month: 'short' }));
  }
  return out;
}

/** Written by dashboard job cards; Jobs tab opens the drawer for this id. */
export const DASHBOARD_OPEN_JOB_ID_KEY = 'ggj_jobs_open_job_id';

function monthKeyMonthsAgo(monthsAgo: number): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - monthsAgo);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function countRecordsInMonth(items: { created_at?: string }[], yyyymm: string): number {
  return items.filter((x) => String(x.created_at ?? '').startsWith(yyyymm)).length;
}

function normalizeMatchPct(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.round(n <= 1 ? n * 100 : n);
}

function extractOptimizedScore(row: Record<string, unknown>): number | null {
  return normalizeMatchPct(
    row.optimizedScore ??
      row.match_score ??
      row.matchScore,
  );
}

function extractInitialScore(row: Record<string, unknown>): number | null {
  return normalizeMatchPct(row.originalScore ?? row.initial_score);
}

function buildComplianceTrendPoints(
  history: Record<string, unknown>[],
  readinessScore: number,
): number[] {
  // History is newest-first; reverse → chronological optimized scores.
  const scores = history
    .map((row) => extractOptimizedScore(row))
    .filter((n): n is number => n != null)
    .reverse();

  if (scores.length >= 2) return scores.slice(-4);

  if (scores.length === 1) {
    const latest = history[0];
    const initial = latest ? extractInitialScore(latest) : null;
    const optimized = scores[0];
    if (initial != null && initial !== optimized) return [initial, optimized];
    if (readinessScore > 0 && readinessScore !== optimized) return [optimized, readinessScore];
    return [Math.max(0, optimized - 10), optimized];
  }

  // No optimization scores — still show readiness as a trend when available.
  if (readinessScore > 0) {
    return [Math.max(0, readinessScore - 12), readinessScore];
  }

  return [];
}

function buildMonthlyBarData(
  monthlyTrends: { month: string; applications: number }[],
  optimizations: { created_at: string }[],
): BarPoint[] {
  const bars: BarPoint[] = [];
  for (let i = 5; i >= 0; i--) {
    const key = monthKeyMonthsAgo(i);
    const trendRow = monthlyTrends[5 - i];
    bars.push({
      opt: countRecordsInMonth(optimizations, key),
      app: trendRow?.applications ?? 0,
      off: 0,
    });
  }
  return bars;
}

function barDataHasActivity(data: BarPoint[]): boolean {
  return data.some((b) => b.opt > 0 || b.app > 0 || b.off > 0);
}

const AVATAR_COLORS: Record<string, string> = {
  A: '#6C47FF', B: '#0ea5e9', C: '#10b981', D: '#f59e0b', E: '#ef4444',
  F: '#8b5cf6', G: '#06b6d4', H: '#84cc16', I: '#f97316', J: '#6C47FF',
  K: '#ec4899', L: '#14b8a6', M: '#6366f1', N: '#0891b2', O: '#7c3aed',
  P: '#059669', Q: '#d97706', R: '#dc2626', S: '#7c3aed', T: '#0369a1',
  U: '#6C47FF', W: '#0ea5e9', X: '#f59e0b', Y: '#10b981', Z: '#6C47FF',
};

// â”€â”€â”€ Theme tokens â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Color palette â€” spec: near-black, never pure black

// Design system — all colors / typography / surfaces are defined in
//   src/assets/styles/design-system.css and consumed through t-* / ds-* utility
//   classes. Raw hex codes in this file are reserved for data-chart colors only.

// â”€â”€â”€ Typography â€” dense heavy-ink dashboard spec â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Hero number letter-spacing is encoded inside .t-hero — kept as a constant
// purely so SVG <text> elements (which can't consume CSS classes) can reuse it.
const HERO_TRACKING: React.CSSProperties = { letterSpacing: '-0.03em' };

/** .dashboard-section-header â€” 12px / 800 / uppercase / 0.06em */
// Locked typography role aliases — point at universal classes.
//   TYP_LABEL       → section DIVIDER above a card        (13px, strong, UPPERCASE)
//   TYP_CARD_LABEL  → small label INSIDE a card           (11px, muted, UPPERCASE)
// Two distinct tiers so the visual hierarchy is preserved.
const TYP_LABEL      = 't-section';
const TYP_CARD_LABEL = 't-label';

/** .dashboard-hero-metric â€” 28px / 800 / -0.03em */
const TYP_HERO       = 't-hero t-num';
const TYP_HERO_SM    = 't-h3 t-num';

/** .dashboard-card-title â€” 14px / 700 / -0.01em */
const TYP_CARD_TITLE = 't-card-title';

/** .dashboard-card-meta â€” 12px / 500 */
const TYP_CARD_META  = 't-meta';

/** .dashboard-tag-text â€” 11px / 600 */
const TYP_TAG        = 't-tag';
const TYP_TAG_MUTE   = 't-tag t-muted';

// â”€â”€â”€ Utilities â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function timeAgo(dateStr: string, t: TFunction): string {
  if (!dateStr) return '\u2014';
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 2) return t('dashboard.overview.timeJustNow', 'just now');
  if (m < 60) return t('dashboard.overview.timeMinutesAgo', { count: m, defaultValue: `${m}m ago` });
  const h = Math.floor(m / 60);
  if (h < 24) return t('dashboard.overview.timeHoursAgo', { count: h, defaultValue: `${h}h ago` });
  const d = Math.floor(h / 24);
  return t('dashboard.overview.timeDaysAgo', { count: d, defaultValue: `${d}d ago` });
}

// â”€â”€â”€ Primitives â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function Card({ children, className = '', style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  return <div className={`ds-card-flush ${className}`} style={style}>{children}</div>;
}

function Label({ children, action }: { children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="ds-section-header px-0.5 shrink-0">
      <p className={TYP_LABEL}>{children}</p>
      {action}
    </div>
  );
}

function CompanyAvatar({ company, size = 28 }: { company: string; size?: number }) {
  const initial = (company || 'J').charAt(0).toUpperCase();
  const bg = AVATAR_COLORS[initial] ?? '#6C47FF';

  return (
    <CompanyLogoShared
      company={company}
      imgClassName="rounded-lg object-contain shrink-0 ds-dashboard-company-logo"
      imgStyle={{ width: size, height: size }}
      fallbackClassName="rounded-lg flex items-center justify-center shrink-0 font-bold"
      fallbackStyle={{
        width: size,
        height: size,
        background: `${bg}15`,
        color: bg,
        fontSize: size * 0.4,
      }}
      alt=""
    />
  );
}

// â”€â”€â”€ Activity Step â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Stepper-style item: vertical connector line, square icon, bullet dot

function ActivityStep({ item, isLast }: { item: ActivityItem; isLast: boolean }) {
  const { t } = useTranslation();
  const meta = item.kind === 'applied'
    ? { label: t('dashboard.overview.activityApplicationSent', 'Application Sent'), Icon: CheckCircle2, color: '#10b981' }
    : item.kind === 'interview'
    ? { label: t('dashboard.overview.activityMatchFound', 'Match Found'), Icon: Briefcase, color: '#f59e0b' }
    : { label: t('dashboard.overview.activityResumeTailored', 'Resume Tailored'), Icon: FileText, color: '#6C47FF' };

  const { Icon, color, label } = meta;

  return (
    <div className="flex items-start gap-2">
      {/* Icon column â€” icon + connector line */}
      <div className="flex flex-col items-center shrink-0" style={{ width: 26 }}>
        <div className="rounded-md flex items-center justify-center shrink-0"
          style={{ width: 26, height: 26, background: `${color}18` }}>
          <Icon className="w-3.5 h-3.5" style={{ color }} />
        </div>
        {!isLast && (
          <div className="flex-1 w-px mt-1" style={{ minHeight: 14, background: `${color}30` }} />
        )}
      </div>
      {/* Text */}
      <div className="min-w-0 pb-2">
        <div className="flex items-center gap-1 leading-none">
          <span className="w-1.5 h-1.5 rounded-full shrink-0 mt-0.5" style={{ background: color }} />
          {/* card-title: 14px / 600 */}
          <p className="t-body t-strong leading-tight truncate">{label}</p>
        </div>
        <p className={`truncate ${TYP_CARD_META} mt-0.5 pl-2.5`}>
          @ {item.company || t('dashboard.overview.jobFallback', 'Job')}
        </p>
        <p className={`${TYP_TAG_MUTE} mt-0.5 pl-2.5`}>{timeAgo(item.date, t)}</p>
      </div>
    </div>
  );
}

/** Split activity list into two columns for the 2-col stepper layout */
function ActivityTimeline({ items }: { items: ActivityItem[] }) {
  const capped = items.slice(0, 6);
  const mid = Math.ceil(capped.length / 2);
  const left  = capped.slice(0, mid);
  const right = capped.slice(mid);

  return (
    <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
      <div className="flex flex-col">
        {left.map((item, i) => (
          <ActivityStep key={item.id} item={item} isLast={i === left.length - 1} />
        ))}
      </div>
      {right.length > 0 && (
        <div className="flex flex-col">
          {right.map((item, i) => (
            <ActivityStep key={item.id} item={item} isLast={i === right.length - 1} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Job Match Card ───────────────────────────────────────────────────────────

function jobMatchToJob(job: JobMatch): Job {
  return {
    id: job.id,
    source: 'peerlist',
    source_channel: job.sourceChannel ?? null,
    title: job.title,
    company: job.company,
    location: job.location,
    url: '',
    created: job.lastSeenAt || job.generatedAt || new Date().toISOString(),
    salary_min: job.salaryMin,
    salary_max: job.salaryMax,
    matchScore: job.score,
    matchedSkills: job.matchedSkills,
    missingSkills: job.missingKeywords,
  };
}

function JobMatchCard({
  job,
  onOpen,
  onExplain,
  isTopMatch = false,
}: {
  job: JobMatch;
  onOpen?: () => void;
  onExplain?: () => void;
  isTopMatch?: boolean;
}) {
  const { t } = useTranslation();
  const pct = Math.round(job.score > 1 ? job.score : job.score * 100);

  const rawKws = job.missingKeywords ?? [];
  const kws = (() => {
    const clean = rawKws.filter(k => k && k.length <= 22 && !k.includes('.') && !k.toLowerCase().startsWith('the '));
    if (clean.length > 0) return clean;
    const extracted = rawKws.slice(0, 3)
      .map(k => k.split(/\s+/).filter(w => w.length > 2 && /^[A-Z]/.test(w)).join(' '))
      .filter(Boolean);
    return extracted.length > 0 ? extracted : [];
  })();

  // Fit label: mirrors Jobs tab thresholds (Strong â‰¥75, Good â‰¥55)
  const fitLabel = pct >= 75
    ? t('dashboard.overview.fitStrong', 'Strong Fit')
    : pct >= 55
      ? t('dashboard.overview.fitGood', 'Good Fit')
      : t('dashboard.overview.fitFair', 'Fair Fit');
  const fitChip  = pct >= 75 ? 'ds-chip ds-chip-ok' : pct >= 55 ? 'ds-chip ds-chip-warn' : 'ds-chip';

  // Format salary range if available
  const salaryStr = job.salaryMin && job.salaryMax
    ? `\u20B9${Math.round(job.salaryMin / 100000)}\u2013${Math.round(job.salaryMax / 100000)}L`
    : job.salaryMin
    ? `\u20B9${Math.round(job.salaryMin / 100000)}L+`
    : null;

  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={!onOpen}
      className={`ds-dashboard-job-card flex flex-col gap-1.5 w-full text-left touch-manipulation transition-opacity hover:opacity-90 disabled:cursor-default disabled:hover:opacity-100${isTopMatch ? ' ds-dashboard-job-card--top-match' : ''}`}
    >
      <div className="flex items-start gap-2">
        <CompanyAvatar company={job.company} size={28} />
        <div className="flex-1 min-w-0">
          {/* Title + fit badge in same row */}
          <div className="flex items-center gap-1.5 min-w-0">
            {/* card-title: 14px / 600 / lh 1.25 */}
            <p className={`${TYP_CARD_TITLE} truncate flex-1`}>{job.title}</p>
            {isTopMatch ? (
              <span className="ds-dashboard-job-card__top-badge shrink-0">
                <Star className="w-2.5 h-2.5" aria-hidden />
                {t('dashboard.overview.topMatchBadge', 'Top match')}
              </span>
            ) : null}
            <span className={`shrink-0 ${fitChip} leading-none`}>{fitLabel}</span>
          </div>
          {/* metadata-text: 12px / 400 */}
          <p className={`${TYP_CARD_META} mt-0.5`}>@ {job.company}</p>
          {/* tag-text: 11px / 500 */}
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            <span className={`${TYP_TAG_MUTE} flex items-center gap-0.5`}>
              <MapPin className="w-2.5 h-2.5 shrink-0" />{job.location || t('dashboard.overview.defaultLocation', 'Bengaluru, India')}
            </span>
            {job.employmentType && (
              <span className={`${TYP_TAG} ds-chip`}>{job.employmentType}</span>
            )}
            {salaryStr && (
              <span className={`${TYP_TAG} ds-chip ds-chip-ok`}>{salaryStr}</span>
            )}
            {job.sourceChannel && (
              <span className={`${TYP_TAG} ds-chip ds-chip-info`}>{job.sourceChannel}</span>
            )}
          </div>
          {/* matched skills â€” tag-text */}
          {job.matchedSkills?.length > 0 && (
            <div className="flex gap-1 mt-1 flex-wrap">
              {job.matchedSkills.slice(0, 3).map(s => (
                <span key={s} className={`${TYP_TAG} ds-chip ds-chip-ok inline-flex items-center gap-0.5`}>
                  <Check className="w-2.5 h-2.5 shrink-0" aria-hidden />
                  {s}
                </span>
              ))}
            </div>
          )}
        </div>
        <MatchScoreButton
          score={pct}
          variant="ring"
          onClick={onExplain}
          className="shrink-0"
        />
      </div>
      {kws.length > 0 && (
        <p className={`${TYP_TAG_MUTE} ds-dashboard-job-card__missing ds-dashboard-job-card__divider pt-1 leading-[1.4]`}>
          <span className={TYP_TAG}>{t('dashboard.overview.missingKeywordsLabel', 'Missing:')} </span>
          {kws.slice(0, 2).join(', ')}
        </p>
      )}
    </button>
  );
}


// â”€â”€â”€ Job matches (same merge strategy as Jobs tab) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function mapPeerlistRowToJobMatch(
  j: Record<string, unknown>,
  rec?: {
    score: number;
    gaps: string[];
    breakdown?: ScoreBreakdown;
    generatedAt?: string;
    whyMatched?: string;
  },
): JobMatch {
  const skills = cleanSkills((j.skills as string[] | null) ?? null);
  const mustHave = cleanSkills((j.must_have_skills as string[] | null) ?? null);
  const matched = mustHave.length > 0 ? mustHave : skills;

  return {
    id:             String(j.id ?? ''),
    title:          String(j.title ?? ''),
    company:        String(j.company ?? ''),
    location:       String(j.location ?? ''),
    score:          rec?.score ?? 0,
    missingKeywords: rec?.gaps ?? [],
    matchedSkills:  matched.slice(0, 4),
    employmentType: j.job_type ? String(j.job_type) : undefined,
    salaryMin:      j.salary_min != null ? Number(j.salary_min) : undefined,
    salaryMax:      j.salary_max != null ? Number(j.salary_max) : undefined,
    sourceChannel:  j.source_channel ? String(j.source_channel) : undefined,
    generatedAt:    rec?.generatedAt,
    lastSeenAt:     j.last_seen_at ? String(j.last_seen_at) : undefined,
    breakdown:      rec?.breakdown,
    whyMatched:     rec?.whyMatched,
  };
}

/** Load top-quality AI-scored job matches — same engine as Jobs tab */
async function fetchDashboardJobMatches(userId: string | null): Promise<JobMatch[]> {
  if (!userId) return [];

  const scoreMap = new Map<string, {
    score: number;
    gaps: string[];
    breakdown?: ScoreBreakdown;
    generatedAt?: string;
    whyMatched?: string;
  }>();

  try {
    const recs = await getTopJobsForUser(userId, 50);
    for (const r of recs) {
      scoreMap.set(String(r.job_id), {
        score: Math.round(Number(r.score)),
        gaps: (r.skill_gaps ?? []) as string[],
        breakdown: r.score_breakdown,
        generatedAt: r.generated_at,
        whyMatched: r.why_matched,
      });
    }
  } catch (err) {
    console.warn('[Dashboard] recommendations:', err);
    return [];
  }

  if (scoreMap.size === 0) return [];

  // Fetch the exact scored job IDs from peerlist_jobs (all sources)
  const scoredIds = [...scoreMap.keys()];
  const { data, error } = await supabase
    .from('peerlist_jobs')
    .select(PEERLIST_JOB_SELECT)
    .eq('is_active', true)
    .in('id', scoredIds);

  if (error) {
    console.warn('[Dashboard] peerlist_jobs:', error.message);
    return [];
  }
  if (!data?.length) return [];

  // Map and attach scores
  const withScores = (data as Record<string, unknown>[]).map(j =>
    mapPeerlistRowToJobMatch(j, scoreMap.get(String(j.id))),
  );

  // Sort by score descending â€” best matches first
  withScores.sort((a, b) => b.score - a.score);

  // Quality tiers â€” show only genuinely good matches:
  // Tier 1: score â‰¥ 60 (Good Fit / Strong Fit)
  // Tier 2: score â‰¥ 40 (if not enough Tier-1)
  // Tier 3: all scored (last resort)
  return withScores.slice(0, 20);
}


// ─── Recommended Actions builder ─────────────────────────────────────────────

function pickDisplayMatches(jobs: JobMatch[]): JobMatch[] {
  const tier1 = jobs.filter((j) => j.score >= 60);
  if (tier1.length >= 3) return tier1.slice(0, 7);
  const tier2 = jobs.filter((j) => j.score >= 40);
  if (tier2.length >= 2) return tier2.slice(0, 7);
  return jobs.slice(0, 7);
}

function buildRecommendedActions(
  t: TFunction,
  jobs: JobMatch[],
  stats: { totalApps: number; interviewCount: number; offerCount: number; followUpsDue: number },
  readinessPct: number,
  applications: JobApplication[],
  onNavigate: (page: DashboardPage) => void,
  openJob: (jobId: string) => void,
): RecommendedAction[] {
  const actions: RecommendedAction[] = [];

  const best = jobs.find((j) => j.score >= 75) ?? jobs[0];
  if (best && best.score >= 55) {
    actions.push({
      id: 'apply-best',
      icon: 'apply',
      text: t('dashboard.greeting.actionApply', {
        score: best.score,
        title: best.title,
        defaultValue: `Apply to this ${best.score}% match`,
      }),
      onClick: () => openJob(best.id),
    });
  }

  const followUp = pickFollowUpCandidate(applications);
  if (followUp) {
    const days = daysSinceDate(followUp.applied_date);
    const company = followUp.company_name?.trim() || t('dashboard.greeting.thisEmployer', 'this employer');
    actions.push({
      id: 'followup',
      icon: 'followup',
      text: t('dashboard.greeting.actionFollowUpCompany', {
        company,
        days,
        defaultValue: `Follow up with ${company} — ${days} days since application`,
      }),
      onClick: () => onNavigate('my-activities'),
    });
  } else if (stats.followUpsDue > 0) {
    actions.push({
      id: 'followup',
      icon: 'followup',
      text: t('dashboard.greeting.actionFollowUp', {
        count: stats.followUpsDue,
        defaultValue: `Follow up on ${stats.followUpsDue} pending applications`,
      }),
      onClick: () => onNavigate('my-activities'),
    });
  }

  if (readinessPct < 85) {
    const target = Math.min(100, readinessPct + 13);
    actions.push({
      id: 'ats-improve',
      icon: 'ats',
      text: t('dashboard.greeting.actionAts', {
        from: readinessPct,
        to: target,
        defaultValue: `Improve your ATS score from ${readinessPct} → ${target}`,
      }),
      onClick: () => onNavigate('agent'),
    });
  }

  if (stats.interviewCount > 0) {
    actions.push({
      id: 'interview-prep',
      icon: 'interview',
      text: t('dashboard.greeting.actionInterviewTomorrow', {
        defaultValue: "Prepare for tomorrow's interview",
      }),
      onClick: () => onNavigate('prepare-for-interview'),
    });
  }

  return actions.slice(0, 4);
}

// â”€â”€â”€ Main Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const DashboardOverview: React.FC<DashboardOverviewProps> = ({ onNavigate }) => {
  const { t, i18n } = useTranslation();
  const { profile } = useUserProfile();
  const locale = i18n.language || 'en';
  const [stats, setStats]            = useState({ totalApps: 0, interviewCount: 0, offerCount: 0, followUpsDue: 0 });
  const [readinessPct, setReadiness] = useState(0);
  const [optCount, setOptCount]      = useState(0);
  const [trendPts, setTrendPts]      = useState<number[]>([]);
  const [barData, setBarData]        = useState<BarPoint[]>([]);
  const [jobs, setJobs]              = useState<JobMatch[]>([]);
  const [activity, setActivity]      = useState<ActivityItem[]>([]);
  const [applicationsByStatus, setApplicationsByStatus] = useState<Record<string, number>>({});
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading]        = useState(true);
  const [userName, setUserName]      = useState<string | null>(null);
  const [streakDays, setStreakDays]  = useState(0);
  const [explainJob, setExplainJob]  = useState<JobMatch | null>(null);
  const [calendarReminders, setCalendarReminders] = useState<
    import('../../../types/objectivesTasks').UserCalendarReminder[]
  >([]);

  const openJobInJobsTab = useCallback(
    (jobId: string) => {
      try {
        sessionStorage.setItem(DASHBOARD_OPEN_JOB_ID_KEY, jobId);
      } catch {
        /* ignore quota */
      }
      onNavigate('jobs');
    },
    [onNavigate],
  );

  const loadJobMatches = useCallback(async (userId: string | null) => {
    const jobMatches = await fetchDashboardJobMatches(userId);
    setJobs(jobMatches);
    return jobMatches;
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id ?? (await getCurrentUserId());

      // Fetch user name for greeting
      if (session?.user) {
        const fallback =
          session.user.user_metadata?.full_name ??
          session.user.user_metadata?.name ??
          (session.user.email ? session.user.email.split('@')[0] : null);
        setUserName(fallback ? String(fallback).split(/\s+/)[0] : null);
      }

      // Fetch streak (best-effort)
      if (userId) {
        supabase
          .from('user_streak')
          .select('current_streak_days')
          .eq('user_id', userId)
          .maybeSingle()
          .then(({ data: streakRow }) => {
            if (streakRow?.current_streak_days) setStreakDays(streakRow.current_streak_days);
          })
          .catch(() => { /* best-effort */ });
      }

      await loadJobMatches(userId);

      if (userId && session) {
        void supabase.functions
          .invoke('match-jobs-for-user', {
            headers: { Authorization: `Bearer ${session.access_token}` },
          })
          .then(async ({ error }) => {
            if (error) return;
            const fresh = await fetchDashboardJobMatches(userId);
            if (fresh.length) setJobs(fresh);
          })
          .catch(() => { /* best-effort */ });
      }

      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().slice(0, 10);

      const [optStats, appStats, readiness, appList, history, reminders] = await Promise.all([
        getOptimizationStats(),
        getApplicationStatistics().catch(() => ({
          totalApplications: 0,
          applicationsByStatus: {} as Record<string, number>,
          monthlyTrends: [] as { month: string; applications: number; responses: number }[],
        })),
        getJobReadiness().catch(() => ({ score: 0 })),
        getJobApplications().then(l => l.slice(0, 80)).catch(() => []),
        getOptimizationHistory(24, 0).catch(() => []),
        userId
          ? getCalendarReminders(userId, { entryFrom: tomorrowStr, entryTo: tomorrowStr }).catch(() => [])
          : Promise.resolve([]),
      ]);

      const byStatus       = (appStats as { applicationsByStatus?: Record<string, number> }).applicationsByStatus ?? {};
      const interviewCount = countInterviews(appList, byStatus);
      const offerCount     = byStatus.offered ?? 0;
      const totalApps      = (appStats as { totalApplications?: number }).totalApplications ?? 0;
      const followUpsDue   = countFollowUpsDue(appList);
      const readScore      = Math.max(0, Math.min(100, (readiness as { score?: number })?.score ?? 0));
      const totalOpts      = (optStats as { total?: number })?.total ?? 0;
      const monthlyTrends  = (appStats as { monthlyTrends?: { month: string; applications: number }[] }).monthlyTrends ?? [];

      const optRows = (history as Record<string, unknown>[]).map((row) => ({
        created_at: String(row.created_at ?? ''),
      }));

      setReadiness(readScore);
      setApplicationsByStatus(byStatus);
      setApplications(appList);
      setCalendarReminders(reminders);
      setStats({ totalApps, interviewCount, offerCount, followUpsDue });
      setOptCount(totalOpts);
      setTrendPts(buildComplianceTrendPoints(history as Record<string, unknown>[], readScore));

      const bars = buildMonthlyBarData(monthlyTrends, optRows);
      setBarData(barDataHasActivity(bars) ? bars : []);

      const JUNK = new Set(['job', 'resume', 'company', 'target', 'not specified', 'job from description', '']);
      const isGood = (t: string, c: string) => !JUNK.has(t.toLowerCase().trim()) && !JUNK.has(c.toLowerCase().trim());

      const items: ActivityItem[] = [];
      appList.forEach((a, i) => {
        const title = a.job_title?.trim() || '';
        const company = a.company_name?.trim() || '';
        if (!isGood(title, company)) return;
        items.push({ id: `a${i}`, kind: 'applied', title, company, date: a.applied_date || new Date().toISOString() });
      });
      history.forEach((o, i) => {
        const row = o as Record<string, unknown>;
        const title = ((row.jobTitle as string) ?? '').trim();
        const company = ((row.companyName as string) ?? '').trim();
        if (!isGood(title, company)) return;
        items.push({ id: `o${i}`, kind: 'optimized', title, company, date: (row.created_at as string) ?? new Date().toISOString() });
      });
      items.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      setActivity(items.slice(0, 6));
    } catch (err) {
      console.error('[Dashboard]', err);
    } finally {
      setLoading(false);
    }
  }, [loadJobMatches]);

  useEffect(() => { loadData(); }, [loadData]);

  // Re-fetch matches when auth hydrates (avoids empty state on first paint)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const uid = session?.user?.id ?? null;
      void loadJobMatches(uid);
    });
    return () => subscription.unsubscribe();
  }, [loadJobMatches]);

  // WhatsApp community banner (dismissible)
  const WA_DISMISS_KEY = 'ggj_wa_community_dismissed';
  const [showWaBanner, setShowWaBanner] = useState(() => {
    try { return localStorage.getItem(WA_DISMISS_KEY) !== '1'; } catch { return true; }
  });
  const dismissWaBanner = useCallback(() => {
    setShowWaBanner(false);
    try { localStorage.setItem(WA_DISMISS_KEY, '1'); } catch { /* noop */ }
  }, []);

  // â”€â”€ Derived values â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const { totalApps, interviewCount, offerCount, followUpsDue } = stats;
  const hasUserActivity = totalApps > 0 || optCount > 0;
  const followUpCount   = followUpsDue;
  const displayJobs     = pickDisplayMatches(jobs);
  const featuredJobs    = displayJobs.slice(0, 3);
  const moreJobs        = displayJobs.slice(3);
  const currentTitle    = (profile?.currentRole || profile?.headline || '').trim();
  const profileReadyPct = computeProfileCompletenessPct(profile);
  const hiddenOps       = buildHiddenOpportunities(
    currentTitle,
    jobListToHiddenSources(jobs),
  );
  const newMatchingJobs = countNewMatchesToday(jobs) || jobs.length;
  const strongMatches = jobs.filter((j) => j.score >= 75).length;
  const greetFirstName = userName ?? profile?.displayName?.split(/\s+/)[0] ?? null;
  const dailySnapshot: DailySnapshot = {
    newMatchingJobs,
    strongMatches,
    applicationsSent: totalApps,
    followUpsDue,
    interviews: interviewCount,
  };
  const dailyBrief = buildDailyBriefViewModel(
    jobs.map((j) => ({
      score: j.score,
      salaryMin: j.salaryMin,
      salaryMax: j.salaryMax,
    })),
    applications,
    calendarReminders,
  );
  const recommendedActions = buildRecommendedActions(
    t,
    displayJobs,
    stats,
    readinessPct,
    applications,
    onNavigate,
    openJobInJobsTab,
  );
  const topMatchIds = new Set(featuredJobs.map((j) => j.id));
  const matchFeedJobs = featuredJobs.length > 0
    ? [...featuredJobs, ...moreJobs]
    : displayJobs;
  const isJobTopMatch = (job: JobMatch) => topMatchIds.has(job.id);

  const monthLabels  = buildRollingMonthLabels(locale);

  // â”€â”€ Render â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center w-full" style={{ minHeight: 'calc(100vh - 48px)' }}>
        <LoadingSpinner size="lg" showText={false} />
      </div>
    );
  }

  return (
    <>
      <motion.div
        key={i18n.language}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className={`ds-page ds-dashboard w-full flex-1 min-h-0 flex flex-col ${Capacitor.isNativePlatform() ? 'h-dvh overflow-hidden' : 'overflow-y-auto'}`}
        style={Capacitor.isNativePlatform() ? { padding: 0 } : undefined}
      >
        {Capacitor.isNativePlatform() && <MobileNativeHeader title={t('dashboard.overview.title')} />}

        <div className={`max-w-[1650px] mx-auto w-full ${Capacitor.isNativePlatform() ? 'p-3' : 'px-4 py-4'}`}>

          {showWaBanner && (
            <div className="ds-dashboard-wa-banner mb-4 rounded-xl border flex items-center gap-3 px-4 py-3">
              <MessageCircle className="w-5 h-5 shrink-0 ds-dashboard-wa-banner__icon" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold ds-dashboard-wa-banner__title">Join our WhatsApp Community</p>
                <p className="text-xs mt-0.5 ds-dashboard-wa-banner__sub">Get instant job alerts, career tips & connect with fellow job seekers.</p>
              </div>
              <a
                href="https://chat.whatsapp.com/DZakClwkCzF4iqvfBYf7g5"
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
                style={{ background: '#25D366' }}
              >
                Join Now
              </a>
              <button type="button" onClick={dismissWaBanner} className="shrink-0 p-1 rounded hover:bg-black/5 dark:hover:bg-white/10 transition-colors" aria-label="Dismiss">
                <X className="w-3.5 h-3.5 ds-dashboard-wa-banner__icon" />
              </button>
            </div>
          )}

          <div className="ds-dashboard-grid flex flex-col gap-4">

            {/* Row 1 — Greeting + KPI strip */}
            <section className="ds-dashboard-hero" data-tour="dashboard-kpi-strip">
              <DashboardGreetingHeader firstName={greetFirstName} streakDays={streakDays} />
              <DailyBriefCard
                brief={dailyBrief}
                onViewMatches={() => onNavigate('jobs')}
              />
              <DashboardKpiStrip snapshot={dailySnapshot} />
            </section>

            {/* Row 2 — Urgent action banner */}
            <div
              data-tour="dashboard-status-bar"
              className={`ds-dashboard-action-banner ${followUpCount > 0 ? 'ds-dashboard-action-banner--urgent' : ''}`}
              role={followUpCount > 0 ? 'button' : undefined}
              tabIndex={followUpCount > 0 ? 0 : undefined}
              onClick={followUpCount > 0 ? () => onNavigate('my-activities') : undefined}
              onKeyDown={followUpCount > 0 ? (e) => { if (e.key === 'Enter' || e.key === ' ') onNavigate('my-activities'); } : undefined}
            >
              <Calendar className={`w-5 h-5 shrink-0 ${followUpCount > 0 ? 't-warn' : 't-muted'}`} aria-hidden />
              {followUpCount > 0 ? (
                <div className="flex-1 min-w-0">
                  <p className="ds-dashboard-action-banner__label">{t('dashboard.overview.actionSuggestedColon')}</p>
                  <p className="ds-dashboard-action-banner__text">
                    {t('dashboard.overview.followUpAwaiting', {
                      defaultValue: 'Follow up on {{count}} applications awaiting a response',
                      count: followUpCount,
                    })}
                  </p>
                </div>
              ) : optCount === 0 ? (
                <p className="ds-dashboard-action-banner__text">{t('dashboard.overview.optimizeForAtsScore')}</p>
              ) : (
                <p className="ds-dashboard-action-banner__text">{t('dashboard.overview.noPendingActions')}</p>
              )}
            </div>

            {/* Row 3 — Main split: actions + matches | ATS + gamification */}
            <div className="ds-dashboard-main-split">

              {/* Left column (~65%) */}
              <div className="ds-dashboard-main-split__primary flex flex-col gap-4 min-w-0">
                <DashboardRecommendedActions actions={recommendedActions} />

                <HiddenOpportunities
                  currentTitle={currentTitle}
                  opportunities={hiddenOps}
                  showPlaceholder={!!currentTitle}
                  onViewJob={(jobId) => openJobInJobsTab(jobId)}
                  onViewAll={() => onNavigate('jobs')}
                />

                <div className="flex flex-col min-w-0" data-tour="dashboard-jobs-section">
                  <div className="flex items-center justify-between mb-1.5">
                    <Label>{t('dashboard.overview.unifiedMatches', 'Your job matches')}</Label>
                    {displayJobs.length > 0 && (
                      <button
                        type="button"
                        onClick={() => onNavigate('jobs')}
                        className="ds-btn ds-btn-link t-tag t-muted -mt-1.5 shrink-0 touch-manipulation"
                      >
                        {t('dashboard.overview.showMyMatches', 'Show me my matches →')}
                      </button>
                    )}
                  </div>
                  <Card className="p-2 ds-dashboard-match-feed">
                    {matchFeedJobs.length > 0 ? (
                      <div className="flex flex-col gap-1.5">
                        {matchFeedJobs.slice(0, 8).map((j) => (
                          <JobMatchCard
                            key={j.id}
                            job={j}
                            isTopMatch={isJobTopMatch(j)}
                            onOpen={() => openJobInJobsTab(j.id)}
                            onExplain={() => setExplainJob(j)}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center gap-2 text-center py-8 t-muted">
                        <Search className="w-7 h-7 opacity-30" aria-hidden />
                        <p className="t-meta font-semibold">{t('dashboard.overview.buildingMatches')}</p>
                        <p className="t-tag max-w-[240px]">{t('dashboard.overview.buildingMatchesHint')}</p>
                        <button type="button" onClick={() => onNavigate('jobs')} className="ds-btn ds-btn-sm ds-btn-primary mt-1">
                          {t('dashboard.overview.browseJobs')}
                        </button>
                      </div>
                    )}
                  </Card>
                </div>
              </div>

              {/* Right column (~35%) */}
              <aside className="ds-dashboard-main-split__sidebar flex flex-col gap-4">
                <Card className="p-4 ds-dashboard-ats-compact">
                  <p className={TYP_CARD_LABEL}>
                    {t('dashboard.overview.atsProfileReadiness', { pct: readinessPct })}
                  </p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className={`${TYP_HERO} shrink-0`} style={HERO_TRACKING}>{readinessPct}%</span>
                    <div className="flex-1 min-w-0">
                      <div className="ds-dashboard-ats-compact__bar" role="progressbar" aria-valuenow={readinessPct} aria-valuemin={0} aria-valuemax={100}>
                        <div
                          className="ds-dashboard-ats-compact__bar-fill"
                          style={{ width: `${readinessPct}%`, background: scoreColor(readinessPct) }}
                        />
                      </div>
                      <span className={`ds-dashboard-readiness-badge mt-2 inline-flex ${
                        readinessPct >= 75 ? 'ds-dashboard-readiness-badge--ok'
                          : readinessPct >= 50 ? 'ds-dashboard-readiness-badge--warn'
                          : 'ds-dashboard-readiness-badge--neutral'
                      }`}>
                        {readinessPct >= 75
                          ? t('dashboard.overview.readinessOnTrack')
                          : readinessPct >= 50
                            ? t('dashboard.overview.readinessNeedsAttention')
                            : t('dashboard.overview.readinessNeedsWork')}
                      </span>
                    </div>
                  </div>
                </Card>

                <DashboardGamification
                  profileReadyPct={profileReadyPct}
                  streakDays={streakDays}
                  totalApplications={totalApps}
                  totalInterviews={interviewCount}
                  actionsToNext={profileReadyPct >= 85 ? 0 : Math.max(1, Math.ceil((85 - profileReadyPct) / 8))}
                  nextMilestonePct={profileReadyPct >= 85 ? 100 : 85}
                />

                {hasUserActivity ? (
                  <Card className="p-3">
                    <p className={`${TYP_CARD_LABEL} mb-2`}>{t('dashboard.overview.pipelineTracker')}</p>
                    <div className="flex flex-col gap-2">
                      <div className="ds-recessed flex items-center gap-2.5 px-2 py-1.5">
                        <Briefcase className="w-3.5 h-3.5 t-muted shrink-0" aria-hidden />
                        <span className="t-tag flex-1">{t('dashboard.gamification.statApps')}</span>
                        <span className="ds-chip t-num">{totalApps}</span>
                      </div>
                      <div className="ds-recessed-warn flex items-center gap-2.5 px-2 py-1.5">
                        <Mic className="w-3.5 h-3.5 t-warn shrink-0" aria-hidden />
                        <span className="t-tag flex-1">{t('dashboard.overview.interviewsLabel')}</span>
                        <span className="ds-chip ds-chip-warn t-num">{interviewCount}</span>
                      </div>
                      <div className="ds-recessed flex items-center gap-2.5 px-2 py-1.5">
                        <Tag className="w-3.5 h-3.5 t-muted shrink-0" aria-hidden />
                        <span className="t-tag flex-1">{t('dashboard.overview.offersLabel')}</span>
                        <span className="ds-chip t-num">{offerCount}</span>
                      </div>
                    </div>
                  </Card>
                ) : null}
              </aside>
            </div>

            {/* Row 4 — Analytics charts */}
            <div className="ds-dashboard-analytics-row grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="p-4 flex flex-col">
                <div className="flex items-start justify-between mb-2 shrink-0">
                  <div>
                    <p className={TYP_CARD_LABEL}>{t('dashboard.overview.complianceTrend')}</p>
                    <p className={`${TYP_TAG_MUTE} mt-0.5`}>{t('dashboard.overview.lastMonth')}</p>
                  </div>
                  {trendPts.length > 1 ? (
                    <span className="ds-chip ds-chip-ok">
                      <span className="w-1.5 h-1.5 rounded-full bg-current shrink-0 opacity-80" />
                      <span className="uppercase tracking-wider">{t('dashboard.overview.statusOk')}</span>
                    </span>
                  ) : null}
                </div>
                <div className="flex-1 min-h-0" style={{ minHeight: 160 }}>
                  {trendPts.length > 1 ? (
                    <DashboardAreaTrendChart points={trendPts} height={160} />
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center gap-1 text-center t-tag t-muted px-2">
                      <p className="t-meta font-semibold">{t('dashboard.overview.noTrendYet')}</p>
                      <p className="t-tag">{t('dashboard.overview.runOptimizationForTrend')}</p>
                    </div>
                  )}
                </div>
              </Card>

              <Card className="p-4 flex flex-col">
                <div className="shrink-0 mb-3">
                  <p className={TYP_CARD_LABEL}>{t('dashboard.overview.jobSearchActivity')}</p>
                  <p className="t-tag t-muted mt-0.5">{t('dashboard.overview.last6Months')}</p>
                </div>
                <div className="w-full flex-1" style={{ minHeight: 160 }}>
                  {barData.length > 0 ? (
                    <DashboardStackedBarChart data={barData} monthLabels={monthLabels} height={200} />
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center gap-1 t-muted">
                      <p className="t-tag">{t('dashboard.overview.noChartActivity')}</p>
                      <p className="t-tag">{t('dashboard.overview.applyToPopulateChart')}</p>
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* Secondary — recent activity (full width) */}
            <div>
              <Label action={
                <button type="button" onClick={() => onNavigate('jobs')} className="ds-btn ds-btn-link t-tag touch-manipulation">
                  {t('dashboard.overview.viewAll')}
                </button>
              }>
                {t('dashboard.overview.recentActivityTimeline')}
              </Label>
              <Card className="p-3">
                {activity.length > 0 ? (
                  <ActivityTimeline items={activity} />
                ) : (
                  <div className="py-4 flex flex-col items-center justify-center gap-1.5 text-center t-muted">
                    <p className="t-meta font-semibold">{t('dashboard.overview.noActivityYet')}</p>
                    <p className="t-tag">{t('dashboard.overview.noActivityHint')}</p>
                  </div>
                )}
              </Card>
            </div>
          </div>

        </div>
      </motion.div>

      <JobMatchExplainerModal
        open={!!explainJob}
        jobTitle={explainJob?.title ?? ''}
        company={explainJob?.company ?? ''}
        breakdown={mapScoreToBreakdown({
          overall: explainJob?.score ?? 0,
          breakdown: explainJob?.breakdown,
          matchedSkills: explainJob?.matchedSkills ?? [],
          missingSkills: explainJob?.missingKeywords ?? [],
          summary: explainJob?.whyMatched,
          profile: {
            experienceYears: profile?.experienceYears,
            industry: profile?.industry,
            currentRole: profile?.currentRole ?? profile?.headline,
          },
        })}
        onClose={() => setExplainJob(null)}
        onOptimize={explainJob ? () => {
          writeAgentOptimizeHandoff(jobMatchToJob(explainJob));
          setExplainJob(null);
          onNavigate('agent');
        } : undefined}
      />
    </>
  );
};

export default DashboardOverview;
